// src/pages/public/TeamMember.js
import React from "react";
import { Helmet } from 'react-helmet-async';
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { TEAM_MEMBERS } from "../../utils/constants";

// Card background: noticeably lighter than the page's dark navy
const CARD_BG    = "#1E2D45";   // lighter navy — lifts off the page
const CARD_INNER = "#243450";   // slightly lighter for nested pills/tags
const BORDER     = "#2E4068";   // visible but not heavy
const TEXT_HEAD  = "#F0F6FF";   // near-white for headings
const TEXT_BODY  = "#B8CCEC";   // light blue-grey for body copy (was too dim before)
const TEXT_DIM   = "#7A99BF";   // subdued labels / dates
const ACCENT     = "#00C9A7";   // keep your teal accent


const card = {
  background: CARD_BG,
  border: `1px solid ${BORDER}`,
  borderRadius: 16,
  padding: 24,
  marginBottom: 24,
};

const Section = ({ title, children, style = {} }) => (
  <div style={{ ...card, ...style }}>
    <h2 style={{ fontSize: "1.0625rem", fontWeight: 700, color: TEXT_HEAD, marginBottom: 16, letterSpacing: "0.01em" }}>
      {title}
    </h2>
    {children}
  </div>
);

const StatusBadge = ({ status }) => {
  const color =
    status === "Published"           ? "#00C9A7" :
    status === "Editorial Assignment"? "#A78BFA" : "#FBBF24";
  return (
    <span style={{ fontSize: "0.7rem", fontWeight: 700, padding: "3px 9px", borderRadius: 99,
      background: `${color}20`, color, border: `1px solid ${color}50`, whiteSpace: "nowrap", flexShrink: 0 }}>
      {status}
    </span>
  );
};

export default function TeamMember() {
  
  const { id } = useParams();
  const member = TEAM_MEMBERS.find(m => m.id === id);

  if (!member) {
    return (
      <div style={{ paddingTop: "var(--header-h)", textAlign: "center", paddingBottom: 64 }}>
        <h1 style={{ fontSize: "2rem", marginTop: 64, color: TEXT_HEAD }}>Team Member Not Found</h1>
        <Link to="/about" className="btn btn-primary" style={{ marginTop: 24 }}>Return to About</Link>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: "var(--header-h)", paddingBottom: 80 }}>
      <Helmet>
        <title>{member.name} - Aimble I.T. Solutions</title>
        <meta name="description" content="Meet the expert team behind Aimble I.T. Solutions. Our dedicated developers, designers, and IT professionals are ready to build your next digital project." />
        <link rel="canonical" href={`https://www.aimbletech.com/team/${member.id}`} />
      </Helmet>
      <div className="container" style={{ maxWidth: 960, margin: "0 auto", padding: "0 24px" }}>

        {/* ── BACK ── */}
        <div style={{ marginBottom: 32 }}>
          <Link to="/about" style={{ color: TEXT_DIM, fontSize: "0.875rem", textDecoration: "none" }}>
            ← Back to Team
          </Link>
        </div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}>

          {/* ── HEADER ── */}
          <div className="tm-header" style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 16, marginBottom: 32 }}>
            <img
              src={member.photo}
              alt={member.name}
              style={{ width: 200, height: 200, borderRadius: "100%", objectFit: "fill", border: `4px solid ${BORDER}` }}
            />
            <div style={{ flex: 1 }}>
              <h1 style={{ fontSize: "2rem", fontWeight: 700, fontFamily: "var(--font-head)", color: TEXT_HEAD, marginBottom: 6 }}>
                {member.name}
              </h1>
              <div style={{ fontSize: "0.95rem", color: ACCENT, fontWeight: 600, marginBottom: 14, lineHeight: 1.5 }}>
                {member.role}
              </div>
              <p style={{ color: TEXT_BODY, lineHeight: 1.7, maxWidth: 600, fontSize: "0.9375rem" }}>
                {member.summary}
              </p>
            </div>
          </div>

          {/* ── GRID ── */}
          <div className="tm-grid">

            {/* LEFT */}
            <div>

              {/* Core Expertise */}
              <Section title="Core Expertise">
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {member.expertise.map((skill, i) => (
                    <span key={i} style={{
                      fontSize: "0.8rem", padding: "5px 12px", borderRadius: 99,
                      background: "#0D4F6C", color: "#7EDECC", border: "1px solid #1A7A6E", fontWeight: 500
                    }}>
                      {skill}
                    </span>
                  ))}
                </div>
              </Section>

              {/* Skills */}
              {member.skills && (
                <Section title="Skills">
                  {Object.entries(member.skills).map(([category, items]) => (
                    <div key={category} style={{ marginBottom: 18 }}>
                      <div style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase",
                        letterSpacing: "0.09em", color: TEXT_DIM, marginBottom: 8 }}>
                        {category.replace(/([A-Z])/g, " $1").trim()}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {items.map((s, i) => (
                          <span key={i} style={{
                            fontSize: "0.78rem", padding: "4px 10px", borderRadius: 99,
                            background: CARD_INNER, color: TEXT_BODY, border: `1px solid ${BORDER}`
                          }}>
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </Section>
              )}

              {/* Education */}
              <Section title="Education">
                {member.education.map((ed, i) => (
                  <div key={i} style={{
                    marginBottom: 16, paddingBottom: 16,
                    borderBottom: i !== member.education.length - 1 ? `1px solid ${BORDER}` : "none"
                  }}>
                    <div style={{ fontWeight: 600, fontSize: "0.9rem", color: TEXT_HEAD }}>{ed.degree}</div>
                    <div style={{ color: TEXT_BODY, fontSize: "0.8125rem", marginTop: 4 }}>{ed.institution}</div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 5 }}>
                      <span style={{ color: TEXT_DIM, fontSize: "0.75rem" }}>{ed.year}</span>
                      {ed.status === "In Progress" && (
                        <span style={{ fontSize: "0.68rem", fontWeight: 700, padding: "1px 7px", borderRadius: 99,
                          background: "#00C9A720", color: ACCENT, border: `1px solid #00C9A750` }}>
                          In Progress
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </Section>

              {/* Certifications */}
              {member.certifications && (
                <Section title="Certifications & Licenses">
                  {member.certifications.map((cert, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12 }}>
                      <span style={{ color: ACCENT, marginTop: 2, flexShrink: 0, fontSize: "0.75rem" }}>✦</span>
                      <span style={{ fontSize: "0.845rem", color: TEXT_BODY, lineHeight: 1.6 }}>{cert}</span>
                    </div>
                  ))}
                </Section>
              )}

            </div>

            {/* RIGHT */}
            <div>

              {/* Experience */}
              <div style={{ ...card, padding: 32 }}>
                <h2 style={{ fontSize: "1.375rem", fontWeight: 700, fontFamily: "var(--font-head)",
                  color: TEXT_HEAD, marginBottom: 24 }}>
                  Professional Experience
                </h2>
                <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                  {member.experience.map((exp, i) => (
                    <div key={i} style={{
                      borderBottom: i !== member.experience.length - 1 ? `1px solid ${BORDER}` : "none",
                      paddingBottom: i !== member.experience.length - 1 ? 24 : 0
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                        flexWrap: "wrap", gap: 8, marginBottom: 6 }}>
                        <div>
                          <h3 style={{ fontSize: "1rem", fontWeight: 600, color: TEXT_HEAD }}>{exp.title}</h3>
                          <div style={{ color: ACCENT, fontSize: "0.85rem", fontWeight: 500, marginTop: 2 }}>
                            {exp.organization}
                          </div>
                        </div>
                        <span style={{ fontSize: "0.775rem", padding: "3px 10px", borderRadius: 99,
                          background: CARD_INNER, color: TEXT_DIM, border: `1px solid ${BORDER}`, whiteSpace: "nowrap" }}>
                          {exp.timeline}
                        </span>
                      </div>
                      <p style={{ color: TEXT_BODY, fontSize: "0.875rem", lineHeight: 1.7, marginTop: 8 }}>
                        {exp.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Publications */}
              {member.publications && member.publications.length > 0 && (
                <div style={{ ...card, padding: 32 }}>
                  <h2 style={{ fontSize: "1.25rem", fontWeight: 700, fontFamily: "var(--font-head)",
                    color: TEXT_HEAD, marginBottom: 20 }}>
                    Publications
                  </h2>
                  <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                    {member.publications.map((pub, i) => (
                      <div key={i} style={{
                        borderBottom: i !== member.publications.length - 1 ? `1px solid ${BORDER}` : "none",
                        paddingBottom: i !== member.publications.length - 1 ? 20 : 0
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "flex-start", gap: 12, marginBottom: 6 }}>
                          <p style={{ fontWeight: 600, fontSize: "0.9rem", lineHeight: 1.55,
                            margin: 0, color: TEXT_HEAD }}>
                            {pub.title}
                          </p>
                          {pub.status && <StatusBadge status={pub.status} />}
                        </div>
                        <div style={{ color: ACCENT, fontSize: "0.8125rem", fontWeight: 500 }}>{pub.journal}</div>
                        {pub.collection && (
                          <div style={{ color: TEXT_DIM, fontSize: "0.775rem", marginTop: 5, fontStyle: "italic" }}>
                            Collection: {pub.collection}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Ongoing Research */}
              {member.ongoingResearch && member.ongoingResearch.length > 0 && (
                <div style={{ ...card, padding: 32, marginBottom: 0 }}>
                  <h2 style={{ fontSize: "1.25rem", fontWeight: 700, fontFamily: "var(--font-head)",
                    color: TEXT_HEAD, marginBottom: 20 }}>
                    Ongoing Research
                  </h2>
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    {member.ongoingResearch.map((r, i) => (
                      <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                        <span style={{ color: ACCENT, fontWeight: 800, fontSize: "0.8rem",
                          marginTop: 3, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <p style={{ color: TEXT_BODY, fontSize: "0.875rem", lineHeight: 1.7, margin: 0 }}>
                          {r.title}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          </div>
        </motion.div>
      </div>

      <style>{`
        .tm-header {
          display: flex;
          gap: 32px;
          align-items: center;
          padding: 32px;
        }
        .tm-grid {
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 32px;
          align-items: start;
        }
        @media (max-width: 768px) {
          .tm-header {
            flex-direction: column;
            text-align: center;
            padding: 24px;
            gap: 16px;
          }
          .tm-header h1 {
            font-size: 1.5rem !important;
          }
          .tm-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}